import {
  ActivityFilterKey,
  CalendarFilter,
  CalendarWindow,
  getActivityFilter,
  Severity,
  useActivityCalendar,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useGrippContext,
} from '@gripp/shared-logic';
import {
  Calendar,
  CalendarEvent,
  CalendarVisibleRange,
  CampaignAggregatedActivityData,
  ColumnData,
  IntegrationRunCard,
  IntegrationRunData,
  InterleavedRow,
  ListCountProvider,
  PanelShell,
  tokens,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';
import { ActivityViewSelector } from './activityViewSelector';
import { useActivityView } from './useActivityView';
import { useRescheduleActivity } from './useRescheduleActivity';

type ActivitiesLocationState = {
  skipAutoSelect?: boolean;
};

type ActivityRow = {
  id: string;
};

/**
 * GraphQL query for the campaign-aggregated activity feed (PR 4 / A1).
 *
 * Nested under `campaign { campaignAggregatedActivities }` — graphback's
 * factory hooks don't auto-unwrap nested namespaces, so we use
 * `useCustomQuery` and read `data.campaign.campaignAggregatedActivities`
 * directly. See AGENTS.md "Graphback-nested queries don't auto-unwrap".
 */
const CAMPAIGN_AGGREGATED_ACTIVITIES = `
  query CampaignAggregatedActivities(
    $filter: CampaignAggregatedActivitiesFilter!
  ) {
    campaign {
      campaignAggregatedActivities(filter: $filter) {
        count
        items {
          campaignId
          title
          oemWorkspaceBrand
          assetCount
          replyCount
          latestActivityAt
          firstMediaThumbnailUrl
          latestMessagePreview
        }
      }
    }
  }
`;

type CampaignAggregatedActivitiesResponse = {
  campaign?: {
    campaignAggregatedActivities?: {
      count?: number;
      items?: CampaignAggregatedActivityData[];
    };
  };
};

/**
 * GraphQL query for the per-run integration sync cards (GeoTab, GRIPP #2887,
 * Plan 0019). `findIntegrationRuns` is a ROOT query (not namespaced), so
 * `useCustomQuery` reads `data.findIntegrationRuns` directly — no unwrap
 * caveat like the nested `integration`/`campaign` queries above.
 *
 * `counts` is a JSON scalar (the worker's SyncCounts) — bounded, enough for the
 * feed card. The unbounded per-asset detail is fetched lazily from a blob via
 * `integration.runDetails` when a run's detail is opened, not in this list query.
 * One row per run; interleaved into the feed at `ranAt`.
 */
const FIND_INTEGRATION_RUNS = `
  query FindIntegrationRuns(
    $filter: IntegrationRunFilter
    $page: PageRequest
    $orderBy: OrderByInput
  ) {
    findIntegrationRuns(filter: $filter, page: $page, orderBy: $orderBy) {
      count
      items {
        id
        provider
        ranAt
        status
        counts
      }
    }
  }
`;

type FindIntegrationRunsResponse = {
  findIntegrationRuns?: {
    count?: number;
    items?: IntegrationRunData[];
  };
};

export const Activities = () => {
  return <ActivitiesBase />;
};

const ActivitiesBase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logger } = useGrippContext();
  const { t } = useTranslation();
  const { issueId, runId: selectedRunId } = useParams();
  const [count, setCount] = useState<number>();
  const skipAutoSelect = (location.state as ActivitiesLocationState | null)
    ?.skipAutoSelect;
  const isActivityRoot = location.pathname.replace(/\/$/, '') === '/activities';

  const workspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const { data: activityFilter, isLoading: activityFilterLoading } =
    useActivityListFilter(ActivityFilterKey.Activity);

  // Originally the resolver 403'd for OEM workspaces, so we mirrored that gate
  // here. While multi-workspace assets are still missing the resolver guard is
  // temporarily relaxed so OEM users can self-test the aggregated card against
  // their own workspace's assets — match that here. Re-add the OEM exclusion
  // when the resolver guard goes back.  Still guard `workspace?.id` — it's
  // undefined on first render (see AGENTS.md).
  //
  // Per ActivityFilterModel convention, the Campaigns toggle is OFF when the
  // marker array contains 'CAMPAIGN'. Hide the aggregated campaign rows when
  // the toggle is OFF — `excludeActivityTypes` in getActivityFilter already
  // hides inline CAMPAIGN issues from the issue list unconditionally.
  const campaignsToggleOff = (activityFilter?.activityTypes ?? []).includes('CAMPAIGN');
  // The aggregated card requires `campaign.read` (resolver enforces it). Viewers
  // without it (e.g. Restricted) get the campaign issues inline instead — see
  // getActivityFilter — so skip the query to avoid a guaranteed FORBIDDEN.
  //
  // Global admins (`*.*`) also get campaigns inline rather than as the card:
  // their feed is cross-workspace, but the card is single-workspace
  // (`viewingWorkspaceId`) and would miss campaigns from every other workspace.
  // Keep this in lockstep with `surfaceCampaignsAsCards` in the shared-ui
  // activityList, which suppresses the inline CAMPAIGN rows for the same set.
  const surfaceCampaignsAsCards =
    (account?.hasPermission('campaign.read') ?? false) &&
    !(account?.isGlobalAdmin() ?? false);
  const aggregatedQueryEnabled =
    !!workspace?.id && !campaignsToggleOff && surfaceCampaignsAsCards;

  // -- List / calendar view (plan 0017 §1, P2.S2 + P2.S3) --------------------
  // The view choice is persisted to localStorage (per-device) and reflected in
  // the `?view=` URL param, with the URL param winning on load — see
  // `useActivityView`. Default `list`. NO server-side preference write. The
  // Calendar owns its own anchor + mode and reports the visible date window
  // back via `onVisibleRangeChange`; we hold that window in state and feed it to
  // `useActivityCalendar`. Seeded from today's month so the hook has a valid
  // range before the Calendar mounts and corrects it.
  const [view, setView] = useActivityView();
  // Seed with today's month as ISO strings (CalendarWindow accepts ISO). This
  // is immediately replaced by the Calendar's first `onVisibleRangeChange` (it
  // fires on mount), and the fetch is gated off until the calendar is shown, so
  // the exact seed never reaches the API — it just keeps the type non-empty.
  const [calendarWindow, setCalendarWindow] = useState<CalendarWindow>(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const onVisibleRangeChange = useCallback((range: CalendarVisibleRange) => {
    setCalendarWindow({ start: range.start, end: range.end });
  }, []);

  // The calendar reuses the SAME filter the list builds (severity, asset,
  // labels, keyword, campaign handling) so both views show an identical set —
  // see ActivityListBase's `getActivityFilter` call. `startDate`/`endDate` come
  // from the window inside the hook, so they are not set here. Guarded on
  // `workspace?.id` (undefined on first render, per AGENTS.md).
  const calendarFilter = useMemo<CalendarFilter>(() => {
    if (!activityFilter || !workspace?.id) return {};
    return getActivityFilter(
      activityFilter,
      workspace.id,
      undefined,
      surfaceCampaignsAsCards
    ) as CalendarFilter;
  }, [activityFilter, workspace?.id, surfaceCampaignsAsCards]);

  // Only fetch when the calendar is actually shown and the workspace resolved.
  const calendarEnabled = view === 'calendar' && !!workspace?.id;
  const {
    events: calendarEvents,
    isFetching: calendarLoading,
    totalCount: calendarTotalCount,
    isTruncated: calendarTruncated,
  } = useActivityCalendar(calendarFilter, calendarWindow, {
    enabled: calendarEnabled,
  });

  // Drag-to-reschedule (P3.S2) is DISABLED FOR NOW — calendar pills are
  // click-to-open only (pointer cursor, no drag). The full drag machinery is
  // kept intact (Calendar `draggable`/`onEventDrop`, the pointer-drag controller
  // in shared-ui, and `useRescheduleActivity` below) so it can be re-enabled by
  // restoring the permission gate and passing it to `draggable` (see the
  // `draggable` prop on <Calendar> below).
  //
  // To re-enable: gate on the SAME permission the updateIssue resolver requires
  // (`issue.update`) plus a resolved workspace — matching the edit-issue routes'
  // `Permissions.issue.update` gate, per the frontend-gates rule. The activity
  // calendar's findIssues is workspace-scoped (no assetId), so every shown
  // activity is in the current workspace; a global admin holds `*.*`, so the
  // Calendar-level (all-or-nothing) gate is correct — no per-event gating needed.
  //   const canRescheduleActivities =
  //     !!workspace?.id && (account?.hasPermission('issue.update') ?? false);
  const rescheduleActivity = useRescheduleActivity();

  const { data: campaignsAggregatedData } =
    useCustomQuery({
      query: CAMPAIGN_AGGREGATED_ACTIVITIES,
      variables: {
        filter: {
          viewingWorkspaceId: workspace?.id ?? '',
          startDate: activityFilter?.startDate || undefined,
          endDate: activityFilter?.endDate || undefined,
        },
      },
      cacheKey: [
        'campaignAggregatedActivities',
        workspace?.id ?? '',
        activityFilter?.startDate ?? '',
        activityFilter?.endDate ?? '',
        campaignsToggleOff ? 'off' : 'on',
      ],
      options: { enabled: aggregatedQueryEnabled },
    }) as { data?: CampaignAggregatedActivitiesResponse };

  const campaignRows: CampaignAggregatedActivityData[] = useMemo(() => {
    const items =
      campaignsAggregatedData?.campaign?.campaignAggregatedActivities?.items ??
      [];
    // Sort newest first by `latestActivityAt`; rows without a timestamp
    // (no replies yet) fall to the end.
    return [...items].sort((a, b) => {
      const aTs = a.latestActivityAt
        ? new Date(a.latestActivityAt).getTime()
        : 0;
      const bTs = b.latestActivityAt
        ? new Date(b.latestActivityAt).getTime()
        : 0;
      return bTs - aTs;
    });
  }, [campaignsAggregatedData]);

  const campaignRowCount = campaignRows.length;

  // Per-run integration sync cards (GeoTab, GRIPP #2887, Plan 0019). Single-
  // workspace, feed-level (`activity.read`), gated on a defined workspace id.
  // Never appears on the per-asset feed (no `assetId` at this layer). Bounded
  // by the active feed date window; runs are 1-2/day at steady state so a
  // generous page cap covers a window without pagination.
  //
  // Integration-run cards render in the "Routines" (blue) category — the same
  // blue the `Severity.Blue` toggle in the Activity Types filter controls (see
  // integrationRunCard.tsx `severityVariant`). The severity filter is applied
  // server-side to `findIssues` only, so gate the run cards here too: hide them
  // whenever blue is deselected, matching the issue list. `severity` defaults to
  // every value, so an undefined (loaded) filter shows them. Wait for the filter
  // to load before deciding — the filter is a react-query-backed storage read, so
  // treating the loading window as "show" would fire the runs query and flash
  // cached cards even when blue was previously deselected.
  const showIntegrationRuns =
    !activityFilterLoading &&
    (!activityFilter?.severity ||
      activityFilter.severity.includes(Severity.Blue));
  const runsQueryEnabled = !!workspace?.id && showIntegrationRuns;

  const {
    data: runsData,
    isError: runsIsError,
    error: runsError,
  } = useCustomQuery({
    query: FIND_INTEGRATION_RUNS,
    variables: {
      filter: {
        workspace: { eq: workspace?.id ?? '' },
        ...(activityFilter?.startDate || activityFilter?.endDate
          ? {
              ranAt: {
                ...(activityFilter?.startDate
                  ? { ge: activityFilter.startDate }
                  : {}),
                ...(activityFilter?.endDate
                  ? { le: activityFilter.endDate }
                  : {}),
              },
            }
          : {}),
      },
      page: { limit: 200, offset: 0 },
      orderBy: { field: 'ranAt', order: 'DESC' },
    },
    cacheKey: [
      'integrationRuns',
      workspace?.id ?? '',
      activityFilter?.startDate ?? '',
      activityFilter?.endDate ?? '',
    ],
    options: { enabled: runsQueryEnabled },
  }) as {
    data?: FindIntegrationRunsResponse;
    isError: boolean;
    error: unknown;
  };

  // Feed must never break on a sync-run failure. On error we render no cards and
  // log — they are supplementary; the issue list is the primary, independently-
  // fetched surface.
  useEffect(() => {
    if (runsIsError) {
      logger.error(
        `Integration sync-run query failed; hiding per-run cards. ${String(
          runsError
        )}`
      );
    }
  }, [runsIsError, runsError, logger]);

  const runInterleavedRows: InterleavedRow[] = useMemo(() => {
    // Blue deselected in the Activity Types filter → hide run cards. Guard here
    // (not just via the query's `enabled`) because react-query keeps the last
    // fetched data cached after `enabled` flips false, so a stale result would
    // otherwise keep rendering.
    if (!showIntegrationRuns || runsIsError) return [];
    const items = runsData?.findIntegrationRuns?.items ?? [];
    return items
      .map((run) => ({ run, ranAt: run.ranAt ? new Date(run.ranAt) : null }))
      // Drop runs whose ranAt is missing or unparseable: an invalid Date (NaN)
      // would break ordering in mergeInterleavedRows and mis-place the card.
      .filter(
        (entry): entry is { run: (typeof items)[number]; ranAt: Date } =>
          entry.ranAt !== null && !Number.isNaN(entry.ranAt.getTime())
      )
      .map(({ run, ranAt }) => ({
        id: `sync-run-${run.id}`,
        sortValue: ranAt,
        // Web opens the detail in the right panel (mobile expands in place).
        render: () => (
          <IntegrationRunCard
            run={run}
            workspaceName={workspace?.name}
            isSelected={selectedRunId === run.id}
            onPress={() =>
              navigate(`/activities/sync-run/${run.id}${location.search}`)
            }
          />
        ),
      }));
  }, [showIntegrationRuns, runsData, runsIsError, workspace?.name, selectedRunId, navigate, location.search]);

  const onIssueSelected = useCallback(
    (id: string) => {
      // Preserve the current query string (notably `?view=`, P2.S3) so opening
      // an activity from the list/calendar doesn't drop the deep-linked view.
      navigate(`/activities/${id}${location.search}`);
    },
    [navigate, location.search]
  );

  // Calendar pill click: an upcoming-alert pill (kind 'alert', id = workflow id)
  // opens the alerts-management screen; an activity pill opens the activity.
  // We look up the kind from the plotted events rather than widening the
  // Calendar/EventPill onPress signature.
  const onCalendarEventOpen = useCallback(
    (id: string) => {
      const ev = calendarEvents.find((e) => e.id === id);
      if (ev?.kind === 'alert') {
        navigate('/alerts-management');
        return;
      }
      onIssueSelected(id);
    },
    [calendarEvents, navigate, onIssueSelected]
  );

  // First-row auto-select: prefer the most recent campaign aggregated row
  // when present (matches sort order). Falls back to the first Issue row
  // returned by `findIssues`, preserving the legacy behavior.
  const handleDataLoaded = useCallback(
    (data: ActivityRow[]) => {
      if (issueId || skipAutoSelect || !isActivityRoot) return;
      if (campaignRowCount > 0) {
        // Campaign aggregated rows are not yet navigable for Dealers (see
        // activityList.tsx CampaignRowsSection note).  Skip auto-select
        // when they lead the list so the right column stays at "no issue
        // selected" instead of auto-opening an Issue from below the fold.
        return;
      }
      const firstIssueId = data?.[0]?.id;
      if (firstIssueId) {
        onIssueSelected(firstIssueId);
      }
    },
    [
      campaignRowCount,
      isActivityRoot,
      issueId,
      onIssueSelected,
      skipAutoSelect,
    ]
  );

  const handleIssueCountChange = useCallback(
    (issueCount: number) => {
      setCount(issueCount + campaignRowCount);
    },
    [campaignRowCount]
  );

  const handleFilterChange = useCallback(() => {
    navigate('/activities/filters', { state: { skipAutoSelect: true } });
  }, [navigate]);

  const leftColumn: ColumnData = {
    title: 'activity.menu.title',
    // In calendar view show the count for the visible window (month/week), not
    // the list's global total; the list view keeps its own running count.
    count: view === 'calendar' ? calendarEvents.length : count,
    // List ↔ calendar selector sits inline in the PanelHeader, right after the
    // "Activity (count)" title (it truncates before the accessory clips).
    titleAccessory: <ActivityViewSelector view={view} onChange={setView} />,
    mainComponent: (
      <View style={styles.mainContent}>
        {view === 'calendar' ? (
          <View style={styles.calendarContainer}>
            {calendarTruncated && (
              <View style={styles.truncationBanner}>
                <Text style={styles.truncationText}>
                  {t('activity.calendar.truncated', {
                    shown: calendarEvents.length,
                    total: calendarTotalCount,
                  })}
                </Text>
              </View>
            )}
            <Calendar
              events={calendarEvents as unknown as CalendarEvent[]}
              initialMode="month"
              showAsset
              selectedId={issueId ?? null}
              onOpenEvent={onCalendarEventOpen}
              onVisibleRangeChange={onVisibleRangeChange}
              loading={calendarLoading}
              // Drag-to-reschedule disabled for now — pills are click-to-open
              // only. Re-enable by passing `draggable={canRescheduleActivities}`
              // (restore the commented permission gate above). `onEventDrop` is
              // left wired (a no-op while `draggable` is false) so re-enabling is
              // a one-line change.
              draggable={false}
              onEventDrop={rescheduleActivity}
            />
          </View>
        ) : (
          <ActivityFoldOneColumn
            onIssueSelected={onIssueSelected}
            showAssetName={true}
            selectedIssue={issueId}
            onDataLoaded={handleDataLoaded}
            onActivityCountChange={handleIssueCountChange}
            hideFilterBar={true}
            campaignRows={campaignRows}
            interleavedRows={runInterleavedRows}
          />
        )}
      </View>
    ),
    onBackButtonPress: () => {
      // Keep the `?view=` param (P2.S3) when returning to the activity root.
      navigate(`/activities${location.search}`, {
        state: { skipAutoSelect: true },
      });
    },
    headerComponent: <ActivityFilters onFilterChange={handleFilterChange} />,
    disableScroll: true,
  };

  // No `outletContextExtras`: the per-run detail panel (IntegrationRunPanel,
  // route activities/sync-run/:runId) fetches its own run via getIntegrationRun,
  // so nothing needs to be published to the outlet context here.
  return (
    <ListCountProvider count={count}>
      <PanelShell leftColumn={leftColumn} />
    </ListCountProvider>
  );
};

const styles = StyleSheet.create({
  mainContent: {
    flex: 1,
    minHeight: 0,
  },
  // Bounded full-height box so the week ScrollView scrolls and the month grid
  // has room for "+N more" (plan §4 / P2.S2).
  calendarContainer: {
    flex: 1,
    minHeight: 0,
    padding: 16,
  },
  // Shown when the server caps the window (more activities than fit in one
  // response). Soft warning band above the grid telling the user to filter.
  truncationBanner: {
    backgroundColor: tokens.colors.semantic.warningLight,
    borderRadius: tokens.borderRadius.sm,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    marginBottom: tokens.spacing.sm,
  },
  truncationText: {
    ...tokens.typography.label.medium,
    color: tokens.colors.semantic.warningDark,
  },
});
